import React, { useEffect, useState, useCallback, useMemo } from "react"
import { useSingleTabEnforcer } from "react-single-tab-enforcer"
import { TabManagerContext } from "./TabManagerContext"

interface TabManagerProviderProps {
  children: React.ReactNode
}

const CHANNEL_NAME = "poolz-tab-manager"
const TAB_PING_MESSAGE = "tab-ping"
const TAB_RESPONSE_MESSAGE = "tab-response"
const TAB_CLAIM_MESSAGE = "tab-claim"
const TAB_RELEASE_MESSAGE = "tab-release"

interface TabMessage {
  type: string
  from: string
  to?: string
}

let fallbackTabCounter = 0
function generateTabId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `tab-${crypto.randomUUID()}`
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const arr = new Uint32Array(4)
    crypto.getRandomValues(arr)
    return `tab-fallback-${Date.now()}-${Array.from(arr)
      .map((n) => n.toString(16))
      .join("")}`
  }
  fallbackTabCounter += 1
  return `tab-fallback-${Date.now()}-${fallbackTabCounter}`
}

export const TabManagerProvider: React.FC<TabManagerProviderProps> = ({ children }) => {
  const [isActiveTab, setIsActiveTab] = useState(true)
  const [isTabLocked, setIsTabLocked] = useState(false)
  const [tabId] = useState(generateTabId)

  const { isLeader } = useSingleTabEnforcer({
    appName: "poolz-interface"
  })

  const postMessage = useCallback((msg: TabMessage) => {
    localStorage.setItem(`tab-manager-msg-${CHANNEL_NAME}`, JSON.stringify({ ...msg, _ts: Date.now() }))
  }, [])

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === `tab-manager-msg-${CHANNEL_NAME}` && e.newValue) {
        try {
          const message: TabMessage & { _ts: number } = JSON.parse(e.newValue)
          if (message.from === tabId) return

          switch (message.type) {
            case TAB_RESPONSE_MESSAGE:
              if (message.to === tabId) {
                setIsActiveTab(false)
                setIsTabLocked(true)
              }
              break
            case TAB_CLAIM_MESSAGE:
              setIsActiveTab(false)
              setIsTabLocked(true)
              break
            case TAB_RELEASE_MESSAGE:
              break
          }
        } catch (err) {
          console.warn("TabManagerProvider: Failed to parse tab message", err)
        }
      }
    }
    window.addEventListener("storage", handleStorage)
    return () => {
      window.removeEventListener("storage", handleStorage)
    }
  }, [tabId])

  useEffect(() => {
    const checkExistingTabs = () => {
      postMessage({
        type: TAB_CLAIM_MESSAGE,
        from: tabId
      })
      postMessage({
        type: TAB_PING_MESSAGE,
        from: tabId
      })
    }
    checkExistingTabs()
  }, [postMessage, tabId])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isActiveTab && isLeader) {
        postMessage({
          type: TAB_RELEASE_MESSAGE,
          from: tabId
        })
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [isActiveTab, tabId, postMessage, isLeader])

  const reloadPage = useCallback(() => {
    if (!isLeader) return
    postMessage({
      type: TAB_CLAIM_MESSAGE,
      from: tabId
    })
    window.location.reload()
  }, [tabId, postMessage, isLeader])

  const claimTab = useCallback(() => {
    if (!isLeader) return false
    postMessage({ type: TAB_CLAIM_MESSAGE, from: tabId })
    return true
  }, [isLeader, postMessage, tabId])

  const releaseTab = useCallback(() => {
    if (!isLeader) return false
    postMessage({ type: TAB_RELEASE_MESSAGE, from: tabId })
    return true
  }, [isLeader, postMessage, tabId])

  const contextValue = useMemo(
    () => ({
      isActiveTab,
      isTabLocked,
      isLeader,
      reloadPage,
      tabId: process.env.NODE_ENV === "test" ? tabId : undefined,
      claimTab,
      releaseTab
    }),
    [isActiveTab, isTabLocked, reloadPage, tabId, claimTab, releaseTab, isLeader]
  )

  return <TabManagerContext.Provider value={contextValue}>{children}</TabManagerContext.Provider>
}

export default TabManagerProvider
